import { cn, RadioGroup, RadioGroupItem } from "@au-schein-de/au-ui-library";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useRevalidator } from "react-router";
import { TransformedImage } from "~/components/transformed-image.component";
import { ProfileHubRatingStars } from "~/features/profile/profile-hub-rating-stars.component";
import { useError } from "~/hooks/use-error.hook";

const RATING_SRC = "/images/rating.png";

const ratingPillClass =
	"flex h-12 min-w-0 flex-1 basis-0 items-center justify-center rounded-xl border-0 bg-gray-200 " +
	"px-4 py-3 text-body-bold text-gray-700 transition-colors " +
	"hover:bg-ansay-background hover:text-ansay-700 data-[state=checked]:border-2 " +
	"data-[state=checked]:border-ansay-main data-[state=checked]:bg-white " +
	"data-[state=checked]:text-gray-900 " +
	"lg:size-10 lg:h-10 lg:flex-none lg:rounded-lg lg:px-3 lg:py-2";

type ProductDetailsUserRatingProps = {
	readonly productId: number;
	readonly initialRating: number | null;
};

export function ProductDetailsUserRating({
	productId,
	initialRating,
}: Readonly<ProductDetailsUserRatingProps>) {
	const { t } = useTranslation("profile");
	const { t: tCommon } = useTranslation("common");
	const fetcher = useFetcher();
	const revalidator = useRevalidator();
	const { showError, hideError } = useError();
	const [expanded, setExpanded] = useState(false);
	const [selectedRating, setSelectedRating] = useState<string | undefined>(
		undefined,
	);
	const pendingSubmissionRef = useRef(false);

	useEffect(() => {
		if (fetcher.data?.error) {
			showError({
				message: t("myOrders.order.rating.error"),
				primaryButton: {
					label: tCommon("close"),
					action: hideError,
				},
			});
		}
	}, [fetcher.data, showError, hideError, t, tCommon]);

	useEffect(() => {
		if (fetcher.state !== "idle" || !pendingSubmissionRef.current) {
			return;
		}
		pendingSubmissionRef.current = false;
		const payload = fetcher.data as
			| { success?: boolean; error?: unknown }
			| undefined;
		if (payload?.error != null) {
			return;
		}
		if (payload?.success !== true) {
			return;
		}
		setExpanded(false);
		setSelectedRating(undefined);
		revalidator.revalidate();
	}, [fetcher.state, fetcher.data, revalidator]);

	const handleRatingChange = (value: string) => {
		setSelectedRating(value);
		pendingSubmissionRef.current = true;
		fetcher.submit(
			{
				productId,
				rating: Number.parseInt(value, 10),
			},
			{ method: "post", action: "/api/product/rate" },
		);
	};

	const showEditor = initialRating == null || expanded;

	return (
		<div
			className={cn(
				"rounded-lg",
				showEditor ? "border-2 border-gray-100 p-3" : "bg-gray-100 px-3 py-2",
			)}
			data-testid="product-details-user-rating"
		>
			{showEditor ? (
				<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
					{initialRating == null ? (
						<div className="flex items-center gap-1">
							<TransformedImage
								src={RATING_SRC}
								alt=""
								width={24}
								height={24}
								className="size-6 shrink-0 object-contain"
								aria-hidden
							/>
							<span className="whitespace-nowrap text-body-sm-emphasis text-gray-700">
								{t("myOrders.order.rating.title")}
							</span>
						</div>
					) : (
						<div className="flex shrink-0 items-center gap-2">
							<span className="text-body-sm text-gray-500">
								{t("reviews.hub.yourRating")}
							</span>
							<div className="flex items-center gap-[3px]">
								<ProfileHubRatingStars
									variant="edit"
									rating={initialRating}
									ariaLabel={t("reviews.hub.ratingEditStarsAria")}
								/>
								<span className="text-body-sm tabular-nums text-gray-700">
									{initialRating}
								</span>
							</div>
						</div>
					)}
					<RadioGroup
						className="flex w-full flex-nowrap justify-between gap-3 md:gap-2 lg:w-auto"
						orientation="horizontal"
						name={`product-details-user-rating-${productId}`}
						value={selectedRating}
						onValueChange={handleRatingChange}
						aria-label={t("reviews.hub.ratingEditStarsAria")}
						required
					>
						{[1, 2, 3, 4, 5].map((score) => (
							<RadioGroupItem
								key={score}
								value={score.toString()}
								size="sm"
								className={ratingPillClass}
								data-testid={`product-details-user-rating-option-${score}`}
							>
								{score}
							</RadioGroupItem>
						))}
					</RadioGroup>
				</div>
			) : (
				<div
					className="flex flex-wrap items-center justify-between gap-3"
					data-testid="product-details-user-rating-summary"
				>
					<div className="flex items-center gap-3">
						<span className="text-body-sm text-gray-500">
							{t("reviews.hub.yourRating")}
						</span>
						<div className="flex items-center gap-1">
							<ProfileHubRatingStars
								rating={initialRating}
								ariaLabel={t("reviews.hub.ratingStarsAria", {
									value: initialRating,
								})}
							/>
							<span className="text-body-sm tabular-nums text-gray-700">
								{initialRating}
							</span>
						</div>
					</div>
					<button
						type="button"
						data-testid="product-details-user-rating-change"
						onClick={() => setExpanded(true)}
						className="inline-flex h-8 cursor-pointer items-center gap-2 px-0 py-3 text-body-sm-emphasis text-ansay-700 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ansay-700 focus-visible:ring-offset-2 md:basis-full lg:basis-auto"
					>
						{t("reviews.hub.changeRating")}
					</button>
				</div>
			)}
		</div>
	);
}
